export interface BrandingConfig {
  appName: string;
  companyName: string;
  logo: {
    light: string;
    dark: string;
  };
  colors: {
    primary: string;
    secondary: string;
    success: string;
    warning: string;
    error: string;
    background: {
      light: string;
      dark: string;
    };
    text: {
      light: string;
      dark: string;
    };
  };
  features: {
    showGitIntegration: boolean;
    showPasswordProtection: boolean;
    showVersioning: boolean;
    showImportExport: boolean;
    maxFileSize: number;
    allowedFileTypes: string[];
  };
  footer: {
    showFooter: boolean;
    copyrightText: string;
    links: Array<{
      label: string;
      url: string;
    }>;
  };
  customCSS: string;
}

let brandingConfig: BrandingConfig | null = null;

export async function loadBrandingConfig(): Promise<BrandingConfig> {
  if (brandingConfig) return brandingConfig;
  
  try {
    // In production, the config would be bundled
    // For now, we'll use the default config with the ability to override
    brandingConfig = getDefaultBranding();
    
    // Try to load from electron if available
    if (window.electronAPI && window.electronAPI.readFile) {
      try {
        const configPath = await window.electronAPI.getPath('userData');
        const configContent = await window.electronAPI.readFile(`${configPath}/branding.config.json`);
        if (configContent) {
          brandingConfig = JSON.parse(configContent);
        }
      } catch (e) {
        // Use default if custom config doesn't exist
      }
    }
    
    return brandingConfig!;
  } catch (error) {
    console.warn('Failed to load branding config, using defaults', error);
    return getDefaultBranding();
  }
}

export function getDefaultBranding(): BrandingConfig {
  return {
    appName: 'Time Files',
    companyName: 'Time-Files',
    logo: {
      light: '/assets/logo-light.svg',
      dark: '/assets/logo-dark.svg'
    },
    colors: {
      primary: '#007acc',
      secondary: '#40a9ff',
      success: '#52c41a',
      warning: '#faad14',
      error: '#f5222d',
      background: {
        light: '#ffffff',
        dark: '#1f1f1f'
      },
      text: {
        light: '#000000',
        dark: '#ffffff'
      }
    },
    features: {
      showGitIntegration: true,
      showPasswordProtection: true,
      showVersioning: true,
      showImportExport: true,
      maxFileSize: 52428800,
      allowedFileTypes: ['.md', '.txt', '.pdf', '.docx']
    },
    footer: {
      showFooter: true,
      copyrightText: '© 2025 Time-Files. All rights reserved.',
      links: []
    },
    customCSS: ''
  };
}

export function applyBrandingColors(config: BrandingConfig, isDarkMode: boolean) {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', config.colors.primary);
  root.style.setProperty('--color-secondary', config.colors.secondary);
  root.style.setProperty('--color-success', config.colors.success);
  root.style.setProperty('--color-warning', config.colors.warning);
  root.style.setProperty('--color-error', config.colors.error);
  root.style.setProperty('--color-background', isDarkMode ? config.colors.background.dark : config.colors.background.light);
  root.style.setProperty('--color-text', isDarkMode ? config.colors.text.dark : config.colors.text.light);
  
  if (config.customCSS) {
    const styleElement = document.getElementById('custom-branding-styles') || document.createElement('style');
    styleElement.id = 'custom-branding-styles';
    styleElement.textContent = config.customCSS;
    if (!document.getElementById('custom-branding-styles')) {
      document.head.appendChild(styleElement);
    }
  }
}